import { t } from 'i18next';

import { DiscountPayloadType, PartFieldOperator } from 'corporate/enums';
import { IDiscount } from 'corporate/types';
import { PricingProfileRuleType } from 'pricing/enums';
import { IUpsertPricingProfileRulePayload } from 'pricing/types';

import { IPricingProfileRuleForm } from './types';

export const mapUpsertPricingProfileRule = (
  structureId: number,
  data: IPricingProfileRuleForm,
  type: PricingProfileRuleType,
  ruleId?: number
): IUpsertPricingProfileRulePayload => {
  return {
    ...(ruleId && { id: ruleId }),
    manufacturerId: data.manufacturer?.id,
    categoryId: data.category?.id,
    structureId,
    discount: data.value,
    type: getPricingProfileRuleType(
      data,
      type === PricingProfileRuleType.MARGIN
        ? DiscountPayloadType.MARGIN
        : DiscountPayloadType.DISCOUNT
    ),
    ...getPricingProfileCriteria(data),
  };
};

export const getPricingProfileRuleType = (
  data: IPricingProfileRuleForm,
  defaultType: DiscountPayloadType
): DiscountPayloadType => {
  if (data.partFieldCriteria?.length || data.companyPartFieldCriteria?.length) {
    return DiscountPayloadType.PART_FIELD;
  }

  if (data.manufacturer?.id) {
    return DiscountPayloadType.MANUFACTURER;
  }

  if (data.category?.id) {
    return DiscountPayloadType.CATEGORY;
  }

  return defaultType;
};

export const getPricingProfileCriteria = (data: IPricingProfileRuleForm) => {
  const result: Pick<
    IUpsertPricingProfileRulePayload,
    'partFieldCriteria' | 'corporatePartFieldCriteria'
  > = {};

  if (data.partFieldCriteria?.length) {
    result.partFieldCriteria = data.partFieldCriteria.map((criteria) => ({
      partFieldId: criteria.partField?.id,
      operator: criteria.operator,
      value: criteria.value,
      uomId: criteria.uom?.id,
    }));
  }

  if (data.companyPartFieldCriteria?.length) {
    result.corporatePartFieldCriteria = data.companyPartFieldCriteria.map((criteria) => ({
      corporatePartFieldName: criteria.companyPartField?.name,
      operator: criteria.operator,
      value: criteria.value,
    }));
  }

  return result;
};

export const getOperatorOptions = () => [
  { value: PartFieldOperator.EQ, label: t('labelEquals') },
  { value: PartFieldOperator.NE, label: t('labelNotEquals') },
  { value: PartFieldOperator.GT, label: t('labelGreaterThan') },
  { value: PartFieldOperator.GTE, label: t('labelGreaterThanOrEqual') },
  { value: PartFieldOperator.LT, label: t('labelLessThan') },
  { value: PartFieldOperator.LTE, label: t('labelLessThanOrEqual') },
  { value: PartFieldOperator.CONTAINS, label: t('labelContains') },
  { value: PartFieldOperator.BEGINS_WITH, label: t('labelBeginsWith') },
];

export const getDefaultValues = <T extends IDiscount>(entity?: T) => {
  return {
    manufacturer: entity ? { id: entity.manufacturerId, name: entity.manufacturerName } : null,
    category: entity ? { id: entity.categoryId, name: entity.categoryName } : null,
    partFieldCriteria:
      entity?.partFieldCriteria?.map((criteria) => ({
        partField: {
          id: criteria.partFieldId,
          name: criteria.partFieldName,
          uoms: criteria.partFieldUoms,
        },
        operator: criteria.operator,
        value: criteria.value,
        uom: criteria.uomId ? { id: criteria.uomId, name: criteria.uomName } : null,
      })) || [],
    companyPartFieldCriteria:
      entity?.corporatePartFieldCriteria?.map((criteria) => ({
        companyPartField: {
          id: criteria.corporatePartFieldName,
          name: criteria.corporatePartFieldName,
        },
        operator: criteria.operator,
        value: criteria.value,
      })) || [],
    value: entity?.discount,
  };
};
